import React from 'react';
import { NavLink } from 'react-router-dom';

// eslint-disable-next-line max-len
export const petLossSupMastheadText = 'We know how hard it is to cope with the loss of a pet, so we\'re here for you during this devastating time. Explore some of Lap of Love\'s pet loss support resources and discover the best ways to begin processing your painful loss.';

export const navItems = [
	{
		name: 'anticipatory',
		title: 'Anticipatory grief',
	},
	{
		name: 'support',
		title: 'Support groups',
	},
	{
		name: 'counseling',
		title: 'Counseling',
	},
	{
		name: 'coping',
		title: 'Coping with pet loss',
	},
];

export const copingBlockTitle = (
	<>
		Coping with the loss of your
		<span className="highlight"> beloved </span>
		pet
	</>
);

// eslint-disable-next-line max-len
export const copingBlockText = 'Grief is different for everyone, including how long they grieve. Some people even feel that moving past their grief means they\’re being disloyal to their pet. But moving forward isn\'t being disloyal, and you\’ll start to know when you\’re ready to begin the healing process. Time really does tend to mend a broken heart, and eventually you\’ll start to know when the time is right for you. Throughout this page, you\'ll find plenty of resources designed to help you both grieve and overcome the loss of your pet, enabling you to move forward and live your life comfortably with the fond memories of your furry friend in your heart.';

export const supportRichText = (
	<>
		<p>
			Pet loss support groups can provide a safe and non-judgmental place for family members to go to be able to share their feelings and
			experiences, whether it be before or after the loss of their beloved pet. Support groups are a place where family members will meet others
			who understand what they're going through, as many times your own family and friends may not completely understand your grief. Many groups
			welcome children as well.
		</p>
		<p>
			To find a pet loss support group in your area, contact your local veterinarian for recommendations or find them online on your veterinary
			hospital’s website or by searching for groups in your area.
		</p>
	</>
);

export const anticipatoryRichText = (
	<>
		<p>
			Caring for a terminally ill or geriatric pet can be a challenge, often involving special care routines, frequent veterinary visits, and
			intense worry. Knowing that the time you have left with your beloved pet is limited can cause stress and anxiety. You may find yourself
			hoping for a natural death, or the “perfect” death.
		</p>
		<p>
			Many pet owners experience anticipatory grief at this stage of their pet’s journey. Anticipatory grief occurs prior to actually losing
			your pet, and consists of a range of emotions including fear, guilt, anxiety, and frustration. Anxiety surrounding the anticipation of
			death is normal, but allowing the anxiety to overwhelm you can interfere with your ability to enjoy the remaining time you have with your
			pet.
		</p>
		<p>
			Being prepared for this stage of your pet’s life is the best way to deal with anticipatory grief, and help your pet. Begin preparing and
			planning for your pet’s passing by asking yourself the following questions:
		</p>
		<h6>How do I envision the last few moments of my pet's life?</h6>
		<p>Where do I want the last few moments to take place? Who should be present? Is there anything I don’t want to happen?</p>
		<h6>How do I feel about euthanasia?</h6>
		<p>Do I understand the process? Do I have questions for my vet?</p>
		<h6>What can I do now so I can look back and think, "This was the perfect passing?" and "I have no regrets?"</h6>
		<p>Are there any “bucket list” items I want to experience with my pet? What can I do to make my pet feel extra special?</p>
		<p>
			While it’s extremely difficult to face the prospect of life without your pet, answering these questions will help you prepare as your
			pet’s time draws near. Thoughtful, advanced planning will help alleviate much of your anxiety so you can focus on providing your pet with
			compassionate physical care. Making your pet’s last days extra special, with lots of love, and their favorite toys, activities, and food
			also may ease your grieving heart.
		</p>
		<h6>
			If you are experiencing anticipatory grief, or find it difficult to answer the above questions, an in-home consultation with
			{' '}
			<NavLink to="/find-a-vet">one of our veterinarians</NavLink>
			{' '}
			may be beneficial.
		</h6>
	</>
);

export const councelingBlockTitle = 'Pet loss counseling';

// eslint-disable-next-line max-len
export const councelingBlockText = 'Sometimes, one-on-one assistance can be the best way to help you when grieving the loss of your beloved companion. And to help you do this in your area, there is a state-by-state listing of counselors across the country. Please note that this is not a comprehensive list and does not include every counselor in every state. If you’re unable to locate a counselor in your area, speak with your veterinarian, as they may have some good suggestions for you.';

export const distTherapyRichText = (
	<>
		<p>
			Not all counseling needs to be away from your home. Distance therapy is available and can be just as helpful.
			{' '}
			<a href="http://www.griefhealing.com/index.htm" target="_blank" className="inline-link" rel="noopener noreferrer">
				Click here
			</a>
			{' '}
			for the website that was created by Marty Tousley, RN, MS, FT, DCC. She has been a hospice counselor for 17 years. Marty is a registered
			nurse with a Master’s in Advanced Psychiatric Mental-Health Nursing, was awarded the Fellow in Thanatology (advanced certification for
			professionals in the fields of grief, loss, and transition) and is a Distance Credentialed Counselor. Her website is designed to help
			those who are anticipating or mourning the loss of their loved ones, regardless of species! The website includes discussion groups,
			healing courses, resources, and a blog to help you find comfort.
		</p>
	</>
);

export const counselingRichText = (
	<>
		<p>
			There may be a university nearby that has its own veterinary hospital and licensed clinical social worker on site. This is a rapidly
			growing field and the veterinary community has made some very important strides to support their clients and fellow animal advocates.
			These counselors are available to the families of patients at university hospitals and many also counsel members in the community. They
			offer a lot of great resources on their sites as well.
		</p>
	</>
);

export const blockRichTitle = (
	<>
		Informational resources to help you
		<span className="highlight"> cope </span>
		with the loss of your pet
	</>
);

export const blockRichText = '';

export const blockRichImgCaptures = [
	{
		name: 'Bink',
		town: 'Herndon, VA',
		title: 'Pet Loss Support Services',
		link: '/our-services/pet-loss-support',
	},
	{
		name: 'Coco',
		town: 'Odessa, FL',
		title: 'Children & Pet Grief Resources',
		link: '/pet-loss-support-resources/children-and-pet-grief-resources',
	},
	{
		name: 'Rockee',
		town: 'Plantation, FL',
		title: 'Beginning to Heal',
		link: '/pet-loss-support-resources/healing-process',
	},
];

export const petLossSupportLinkText = (
	<>
		<a className="inline-link midnightblue" href="/our-services/pet-loss-support" target="_blank" rel="noopener noreferrer">
			<b>Explore Lap of Love's Pet Loss Support Groups</b>
		</a>
	</>
);
